import { useSWR } from '@ori/fetching';
import type { Breadcrumb } from '@ori/presentation-components';
import type { FC, ReactElement } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useConfiguration } from '../contexts';
import type { ApiError, TopAreaApiResponse, TopAreaState } from '../models';
import { fetcher } from '../utils';
import { buildSetters } from './buildSetters';
import { defaultState } from './defaultState';
import { topAreaStoreReducer } from './topAreaStoreReducer';

export interface TopAreaStore extends TopAreaState {
  setIsSubnavNested: (value: boolean) => void;
}

const topAreaStoreContext = createContext<TopAreaStore | undefined>(undefined);
const { Provider } = topAreaStoreContext;

export const TopAreaStoreProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const { bannerApiUrl, breadcrumbsApiUrl, pageId, currentLanguage } = useConfiguration();
  const [topAreaStore, dispatchAction] = useReducer(topAreaStoreReducer, defaultState);
  const {
    current: { setBannerData, setBreadCrumbsData, setIsSubnavNested },
  } = useRef(buildSetters(dispatchAction));
  const { data: breadcrumbs, error: breadcrumbsError } = useSWR<Breadcrumb[] | undefined, ApiError>(
    [breadcrumbsApiUrl, pageId, 'breadcrumbs'],
    async (apiUrl: string, id: string) => {
      if (apiUrl) {
        const url = new URL(apiUrl);
        url.searchParams.set('itemId', id);
        url.searchParams.set('sc_lang', currentLanguage);

        return fetcher(url.toString());
      }

      return undefined;
    },
  );
  useEffect(() => {
    if (breadcrumbs) {
      setBreadCrumbsData(breadcrumbs);
    }
    if (breadcrumbsError) {
      // eslint-disable-next-line no-console -- log error
      console.error(breadcrumbsError);
    }
  }, [breadcrumbs, breadcrumbsError, setBreadCrumbsData]);
  const { data: banner, error: bannerError } = useSWR<TopAreaApiResponse | undefined, ApiError>(
    [bannerApiUrl, pageId, 'banner'],
    async (apiUrl: string, id: string) => {
      if (apiUrl) {
        const url = new URL(apiUrl);
        url.searchParams.set('pageId', id);
        url.searchParams.set('sc_lang', currentLanguage);

        return fetcher(url.toString());
      }

      return undefined;
    },
  );
  useEffect(() => {
    if (banner) {
      setBannerData(banner);
    }
    if (bannerError) {
      // eslint-disable-next-line no-console -- log error
      console.error(bannerError);
    }
  }, [banner, bannerError, setBannerData]);
  const providerValue = useMemo(() => ({ ...topAreaStore, setIsSubnavNested }), [setIsSubnavNested, topAreaStore]);

  return <Provider value={providerValue}>{children}</Provider>;
};

export const useTopAreaStore = () => {
  const context = useContext(topAreaStoreContext);
  if (context) {
    return context;
  }
  throw new Error('TopAreaStore context not initialised.');
};
